/**
 * LAYOUT COMPONENT
 *
 * Main layout wrapper for the Home Language Survey application.
 * Handles:
 * - Navigation structure and routing
 * - Authentication state management
 * - Data export functionality
 * - User profile and sign-in/out controls
 * - Responsive navigation menu
 */

import React from 'react'
import { Dropdown } from 'react-bootstrap'
import { Link, Outlet } from '@tanstack/react-router'
import VermontLogo from './VermontLogo'
import { SignInButton } from './component/survey/SignInButton'
import { AuthenticatedTemplate, UnauthenticatedTemplate, useIsAuthenticated, useMsal } from '@azure/msal-react'
import { SignOutButton } from './component/survey/SignOutButton'
import ProfileContent from './component/survey/ProfileContent'
import { loginRequest } from './authConfig'
import { api } from './component/shared/ApiProvider'

const Layout = () => {
  // Get authentication state and MSAL instance for token management
  const isAuthenticated = useIsAuthenticated()
  const { instance, accounts } = useMsal()

  /**
   * Handle CSV export of student data
   * Requires authentication and proper token management
   * Downloads student survey data as CSV file for administrative use
   */
  const handleExportData = async () => {
    try {
      // Validate user authentication status
      if (!accounts || accounts.length === 0) {
        alert('No authenticated account found. Please refresh the page and sign in again.')
        return
      }

      // Acquire fresh token to ensure valid authentication
      const request = {
        ...loginRequest,
        account: accounts[0],
      }

      const tokenResponse = await instance.acquireTokenSilent(request)

      // Set explicit authorization header for the export request
      const authHeaders = {
        Authorization: `Bearer ${tokenResponse.idToken}`,
      }

      const csvResponse = await api.get('/students/export', {
        responseType: 'blob', // Important for file downloads
        timeout: 60000, // 60 second timeout for larger datasets
        headers: authHeaders,
      })

      // Check if we got a valid CSV response
      if (csvResponse.data.size === 0) {
        alert('Received empty response from server')
        return
      }

      // Create blob link to download file
      const url = window.URL.createObjectURL(new Blob([csvResponse.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'students_export.csv')
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error exporting data:', error)
      console.error('Error details:', error.response?.status, error.response?.data)

      if (error.name === 'InteractionRequiredAuthError') {
        alert('Authentication session expired. Please refresh the page and sign in again.')
      } else if (error.code === 'ECONNABORTED') {
        alert('Request timed out. The server may be slow or unresponsive.')
      } else if (error.response?.status === 401) {
        alert('Authentication failed. Please refresh the page and try again.')
      } else {
        alert(`Failed to export data: ${error.message}. Please check the console for details.`)
      }
    }
  }

  return (
    <div>
      <div className="container mb-0">
        <div className="row align-items-center py-2">
          <div className="col-9 align-items-center">
            <VermontLogo />
          </div>
          <div className="col-3 d-flex justify-content-center align-items-center">
            <UnauthenticatedTemplate>
              <SignInButton />
            </UnauthenticatedTemplate>
            <AuthenticatedTemplate>
              <Dropdown>
                <Dropdown.Toggle variant="secondary" id="menu-dropdown">
                  Menu
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  <Dropdown.Item as={Link} to="/" style={{ textDecoration: 'none' }} className="pr-3">
                    Home
                  </Dropdown.Item>
                  <Dropdown.Item as={Link} to="/process" style={{ textDecoration: 'none' }} className="pr-3">
                    Review Surveys
                  </Dropdown.Item>
                  <Dropdown.Item as={Link} to="/studentsearch" style={{ textDecoration: 'none' }} className="pr-3">
                    Student Search
                  </Dropdown.Item>
                  <Dropdown.Item as={Link} to="/languagetable" style={{ textDecoration: 'none' }} className="pr-3">
                    ISO Languages
                  </Dropdown.Item>
                  <Dropdown.Item onClick={handleExportData} style={{ textDecoration: 'none', cursor: 'pointer' }} className="pr-3">
                    Export Data
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <ProfileContent />
                  <Dropdown.Divider />
                  <Dropdown.Item as={Link} to="#" style={{ textDecoration: 'none' }} className="pr-3">
                    <SignOutButton />
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </AuthenticatedTemplate>
          </div>
        </div>
        <Outlet />
      </div>
    </div>
  )
}
export default Layout
